import { useCallback, useEffect, useRef, useState } from "react";

import type { PostUploadContext } from "./chatMessages";
import {
  buildPostUploadMessage,
  CATEGORY_FRIENDLY_NAMES,
  getNextSuggestion,
  pickGreeting,
} from "./chatMessages";
import type { ChatMessage, DocumentState } from "./types";
import { EMPTY_DOCUMENT_STATE } from "./types";
import type { DocumentUploadState } from "./useDocumentUpload";
import { useDocumentUploads } from "./useDocumentUpload";

const TOTAL_CONTEXT_AREAS = 5;

const CATEGORY_TO_STATE_KEY: Record<string, keyof DocumentState> = {
  CURRENT_INTERVIEW_PROCESS: "currentInterviewProcess",
  IDEAL_CANDIDATE_PROFILE: "idealCandidateProfile",
  CULTURE_AND_TEAM_CONTEXT: "cultureTeamContext",
  PAST_INTERVIEW_MATERIAL: "pastInterviewTranscripts",
};

export type PendingDuplicateJd = {
  existingUploadId: string;
  existingFileName: string;
  newUploadId: string;
  newFileName: string;
};

type UseAgenticChatParams = {
  displayName: string;
  aiInterviewerId?: string | null;
};

type UseAgenticChatResult = {
  messages: ChatMessage[];
  documentState: DocumentState;
  jobDescriptionProvided: boolean;
  isStreaming: boolean;
  isInitializing: boolean;
  isTyping: boolean;
  error: string | null;
  uploads: DocumentUploadState[];
  pendingDuplicateJd: PendingDuplicateJd | null;
  sendUserMessage: (text: string) => void;
  sendFileMessage: (file: File) => void;
  sendRecordingMessage: (
    callId: string,
    callName: string,
    positionTitle?: string | null
  ) => void;
  removeUpload: (uploadId: string) => void;
  resolveDuplicateJd: (choice: "keep-existing" | "replace") => void;
};

export function useAgenticChat({
  displayName,
  aiInterviewerId = null,
}: UseAgenticChatParams): UseAgenticChatResult {
  const usedVariantsRef = useRef<Set<number>>(new Set());

  const [messages, setMessages] = useState<ChatMessage[]>(() => [
    {
      id: "seed",
      role: "assistant",
      content: pickGreeting(displayName, usedVariantsRef.current),
    },
  ]);
  const [isTyping, setIsTyping] = useState(false);
  const [documentState, setDocumentState] =
    useState<DocumentState>(EMPTY_DOCUMENT_STATE);
  const [jobDescriptionProvided, setJobDescriptionProvided] = useState(false);
  const [pendingDuplicateJd, setPendingDuplicateJd] =
    useState<PendingDuplicateJd | null>(null);

  const jobDescriptionProvidedRef = useRef(false);
  jobDescriptionProvidedRef.current = jobDescriptionProvided;

  const documentStateRef = useRef<DocumentState>(EMPTY_DOCUMENT_STATE);
  documentStateRef.current = documentState;

  const pendingDuplicateJdRef = useRef<PendingDuplicateJd | null>(null);
  pendingDuplicateJdRef.current = pendingDuplicateJd;

  const respondTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastCompletedCategoryRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (respondTimerRef.current) clearTimeout(respondTimerRef.current);
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    };
  }, []);

  const getFillCount = useCallback((): number => {
    const ds = documentStateRef.current;
    const jd = jobDescriptionProvidedRef.current;
    return [
      jd,
      ds.currentInterviewProcess,
      ds.idealCandidateProfile,
      ds.cultureTeamContext,
      ds.pastInterviewTranscripts,
    ].filter(Boolean).length;
  }, []);

  const scheduleResponse = useCallback(
    (category: string) => {
      if (respondTimerRef.current) clearTimeout(respondTimerRef.current);
      if (typingTimerRef.current) {
        clearTimeout(typingTimerRef.current);
        setIsTyping(false);
      }

      lastCompletedCategoryRef.current = category;

      const waitDelay = 2400 + Math.random() * 1600;
      respondTimerRef.current = setTimeout(() => {
        setIsTyping(true);

        const typingDelay = 1200 + Math.random() * 800;
        typingTimerRef.current = setTimeout(() => {
          const fillCount = getFillCount();
          const allFilled = fillCount >= TOTAL_CONTEXT_AREAS;
          const isJd = lastCompletedCategoryRef.current === "JOB_DESCRIPTION";
          const suggestion = getNextSuggestion(
            documentStateRef.current,
            jobDescriptionProvidedRef.current
          );

          const ctx: PostUploadContext = {
            docTypeFriendly:
              CATEGORY_FRIENDLY_NAMES[lastCompletedCategoryRef.current ?? ""] ??
              "document",
            fillCount,
            totalAreas: TOTAL_CONTEXT_AREAS,
            suggestedFriendly: suggestion?.friendly ?? null,
            suggestedDescription: suggestion?.description ?? null,
            isJd,
            hasJd: jobDescriptionProvidedRef.current,
            allFilled,
          };

          const content = buildPostUploadMessage(ctx, usedVariantsRef.current);
          setMessages((prev) => [
            ...prev,
            { id: crypto.randomUUID(), role: "assistant", content },
          ]);
          setIsTyping(false);
        }, typingDelay);
      }, waitDelay);
    },
    [getFillCount]
  );

  const handleUploadComplete = useCallback(
    (upload: DocumentUploadState) => {
      const category = upload.analysis?.category;
      if (category === "JOB_DESCRIPTION") {
        if (jobDescriptionProvidedRef.current) {
          const existingJd = uploadsRef.current.find(
            (u) =>
              u.analysis?.category === "JOB_DESCRIPTION" &&
              u.phase === "complete" &&
              u.id !== upload.id
          );
          if (existingJd) {
            const pending: PendingDuplicateJd = {
              existingUploadId: existingJd.id,
              existingFileName: existingJd.fileName,
              newUploadId: upload.id,
              newFileName: upload.fileName,
            };
            setPendingDuplicateJd(pending);
            pendingDuplicateJdRef.current = pending;
            return;
          }
        }
        setJobDescriptionProvided(true);
        jobDescriptionProvidedRef.current = true;
      }
      if (category === "CURRENT_INTERVIEW_PROCESS") {
        setDocumentState((prev) => ({
          ...prev,
          currentInterviewProcess: true,
        }));
      } else if (category === "IDEAL_CANDIDATE_PROFILE") {
        setDocumentState((prev) => ({
          ...prev,
          idealCandidateProfile: true,
        }));
      } else if (category === "CULTURE_AND_TEAM_CONTEXT") {
        setDocumentState((prev) => ({
          ...prev,
          cultureTeamContext: true,
        }));
      } else if (category === "PAST_INTERVIEW_MATERIAL") {
        setDocumentState((prev) => ({
          ...prev,
          pastInterviewTranscripts: true,
        }));
      }

      if (category) {
        scheduleResponse(category);
      }
    },
    [scheduleResponse]
  );

  const { uploads, startUpload, startRecordingUpload, removeUpload } =
    useDocumentUploads({
      aiInterviewerId,
      onUploadComplete: handleUploadComplete,
    });

  const uploadsRef = useRef(uploads);
  uploadsRef.current = uploads;

  const sendUserMessage = useCallback((text: string) => {
    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: text,
    };
    setMessages((prev) => [...prev, userMessage]);
  }, []);

  const sendFileMessage = useCallback(
    (file: File) => {
      const alreadyUploaded = uploadsRef.current.some(
        (u) => u.fileName === file.name && u.phase !== "error"
      );
      if (alreadyUploaded) {
        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: "assistant",
            content: `You already uploaded **${file.name}**. If you'd like to replace it, remove the existing upload first.`,
          },
        ]);
        return;
      }

      const uploadId = startUpload(file);
      setMessages((prev) => [
        ...prev,
        { id: crypto.randomUUID(), role: "user", content: "", uploadId },
      ]);
    },
    [startUpload]
  );

  const sendRecordingMessage = useCallback(
    (callId: string, callName: string, positionTitle?: string | null) => {
      const uploadId = startRecordingUpload(callId, callName, positionTitle);
      setMessages((prev) => [
        ...prev,
        { id: crypto.randomUUID(), role: "user", content: "", uploadId },
      ]);
    },
    [startRecordingUpload]
  );

  const handleRemoveUpload = useCallback(
    (uploadId: string) => {
      const removed = uploadsRef.current.find((u) => u.id === uploadId);
      removeUpload(uploadId);
      setMessages((prev) => prev.filter((m) => m.uploadId !== uploadId));

      if (!removed?.analysis?.category) return;

      const remaining = uploadsRef.current.filter(
        (u) => u.id !== uploadId && u.phase === "complete"
      );

      if (removed.analysis.category === "JOB_DESCRIPTION") {
        const hasOtherJd = remaining.some(
          (u) => u.analysis?.category === "JOB_DESCRIPTION"
        );
        if (!hasOtherJd) {
          setJobDescriptionProvided(false);
          jobDescriptionProvidedRef.current = false;
        }
      }

      const stateKey = CATEGORY_TO_STATE_KEY[removed.analysis.category];
      if (stateKey) {
        const hasOther = remaining.some(
          (u) => u.analysis?.category === removed.analysis?.category
        );
        if (!hasOther) {
          setDocumentState((prev) => ({ ...prev, [stateKey]: false }));
        }
      }
    },
    [removeUpload]
  );

  const resolveDuplicateJd = useCallback(
    (choice: "keep-existing" | "replace") => {
      const pending = pendingDuplicateJdRef.current;
      if (!pending) return;

      if (choice === "keep-existing") {
        handleRemoveUpload(pending.newUploadId);
      } else {
        handleRemoveUpload(pending.existingUploadId);
      }

      setPendingDuplicateJd(null);
      pendingDuplicateJdRef.current = null;
    },
    [handleRemoveUpload]
  );

  return {
    messages,
    documentState,
    jobDescriptionProvided,
    isStreaming: false,
    isInitializing: false,
    isTyping,
    error: null,
    uploads,
    pendingDuplicateJd,
    sendUserMessage,
    sendFileMessage,
    sendRecordingMessage,
    removeUpload: handleRemoveUpload,
    resolveDuplicateJd,
  };
}
